import { useState, type FormEvent } from "react";
import type { InteriorBoard } from "@townsy/contracts";
import { errorMessage, isApiError } from "../api/client";
import { patchInteriorBoard } from "../api/endpoints";
import { Dialog } from "./Dialog";

export const BOARD_TITLE_MAX = 60;
export const BOARD_BODY_MAX = 280;

/**
 * Store board editor (ADR-0004 #3): plain text only (title <= 60, body <= 280), shown on the TV
 * inside the store. Sends `expectedVersion` so a concurrent edit is reported instead of
 * overwritten. The server sanitises and may refuse text; links are never clickable in town.
 */
export function BoardEditorDialog({
  propertyId,
  businessName,
  board,
  onClose,
  onSaved,
  onReloadLatest,
}: {
  propertyId: string;
  businessName: string;
  board: InteriorBoard | null;
  onClose(): void;
  onSaved(board: InteriorBoard): void;
  /** Fetch the latest property (after a version conflict); resolves with the newest board. */
  onReloadLatest(): Promise<InteriorBoard | null>;
}) {
  const [title, setTitle] = useState(board?.title ?? "");
  const [body, setBody] = useState(board?.body ?? "");
  const [baseVersion, setBaseVersion] = useState(board?.version ?? 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [latest, setLatest] = useState<InteriorBoard | null>(null);

  const titleTrim = title.trim();
  const titleOver = title.length > BOARD_TITLE_MAX;
  const bodyOver = body.length > BOARD_BODY_MAX;
  const invalid = !titleTrim || titleOver || bodyOver;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (invalid || busy) return;
    setBusy(true);
    setError(null);
    setConflict(false);
    try {
      const res = await patchInteriorBoard(propertyId, { title: titleTrim, body: body.trim(), expectedVersion: baseVersion });
      onSaved(res.board);
    } catch (err) {
      if (isApiError(err) && err.code === "PROPERTY_VERSION_CHANGED") {
        setConflict(true);
        setError("Someone else changed this board while you were editing. Load the latest text, then apply your changes again.");
      } else if (isApiError(err) && (err.code === "VALIDATION_FAILED" || err.status === 422)) {
        setError(`The text was not accepted: ${err.message || "check the title and message."}`);
      } else if (isApiError(err) && (err.code === "FORBIDDEN" || err.status === 403)) {
        setError("You can no longer edit this store's board (ownership or permissions changed).");
      } else if (isApiError(err) && err.status === 401) {
        setError("Sign in again to edit the board.");
      } else {
        setError(errorMessage(err, "The board could not be saved. Try again."));
      }
    } finally {
      setBusy(false);
    }
  };

  const loadLatest = async () => {
    setBusy(true);
    try {
      const latest = await onReloadLatest();
      // Keep the visitor's draft; show what is saved now so saving again is a deliberate choice.
      setLatest(latest ?? { title: "", body: "", version: 0, updatedAt: "" });
      setBaseVersion(latest?.version ?? 0);
      setConflict(false);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load the latest board."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Store board · ${businessName}`} onClose={() => !busy && onClose()} describedBy="board-editor-hint">
      <form className="auth-forms" onSubmit={(e) => void onSubmit(e)} data-testid="board-editor">
        <p id="board-editor-hint" className="hint" style={{ margin: 0 }}>
          Shown on the TV inside your store. Plain text only; links are not clickable. Moderators can remove text that breaks the rules.
        </p>
        <label className="field">
          Title
          <input
            type="text"
            name="boardTitle"
            value={title}
            maxLength={BOARD_TITLE_MAX + 20}
            required
            onChange={(e) => setTitle(e.target.value)}
            aria-invalid={titleOver || (!titleTrim && title.length > 0)}
            aria-describedby="board-title-count"
          />
          <span id="board-title-count" className={`chat-counter${titleOver ? " over" : ""}`}>
            {title.length}/{BOARD_TITLE_MAX}
          </span>
        </label>
        <label className="field">
          Message
          <textarea
            name="boardBody"
            rows={5}
            value={body}
            maxLength={BOARD_BODY_MAX + 40}
            onChange={(e) => setBody(e.target.value)}
            aria-invalid={bodyOver}
            aria-describedby="board-body-count"
          />
          <span id="board-body-count" className={`chat-counter${bodyOver ? " over" : ""}`}>
            {body.length}/{BOARD_BODY_MAX}
          </span>
        </label>
        {latest ? (
          <div className="notice notice-info small" data-testid="board-latest">
            <strong>Saved now:</strong> {latest.version === 0 ? "(no message yet)" : `${latest.title} — ${latest.body || "(no message)"}`}
            <div style={{ marginTop: 6 }} className="row">
              <span>Save to replace it with your text, or</span>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setTitle(latest.title);
                  setBody(latest.body);
                  setLatest(null);
                }}
              >
                Use the saved text
              </button>
            </div>
          </div>
        ) : null}
        {error ? (
          <div className={`notice ${conflict ? "notice-warning" : "notice-danger"}`} role="alert" data-testid="board-error">
            {error}
            {conflict ? (
              <div style={{ marginTop: 6 }}>
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void loadLatest()}>
                  Load latest text
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={busy || invalid || conflict}>
            {busy ? "Saving…" : "Save board"}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}
